/**
 * Comparison Tab Component
 * Displays user vs agent speech analysis comparison
 */

import { Chip, Grid } from '@mui/material'
import Icon from '@mui/material/Icon'

import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import VerticalBarChart from 'modules/Charts/BarCharts/VerticalBarChart'
import LoadingComponent from 'modules/Loading'

import { ComparisonTabProps } from './types'

function ComparisonTab({
	comparisonData,
	isLoading,
	userChartData,
	agentChartData,
}: ComparisonTabProps): JSX.Element {
	if (isLoading) {
		return <LoadingComponent type="box" height={400} />
	}

	if (!comparisonData) {
		return (
			<MDBox p={3} textAlign="center">
				<MDTypography color="text">No comparison data available</MDTypography>
			</MDBox>
		)
	}

	return (
		<Grid container spacing={3}>
			{/* Summary Stats */}
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<MDBox
							p={3}
							borderRadius="lg"
							sx={{
								backgroundColor: 'rgba(33, 150, 243, 0.05)',
								border: '1px solid rgba(33, 150, 243, 0.2)',
							}}
						>
							<MDTypography variant="h6" color="info" mb={2}>
								<Icon sx={{ verticalAlign: 'middle', mr: 1 }}>person</Icon>
								User Speech Analysis
							</MDTypography>
							<Grid container spacing={2}>
								<Grid item xs={6}>
									<MDTypography variant="caption" color="text">
										Total Words
									</MDTypography>
									<MDTypography variant="h4">
										{comparisonData.userAnalysis.totalWords.toLocaleString()}
									</MDTypography>
								</Grid>
								<Grid item xs={6}>
									<MDTypography variant="caption" color="text">
										Transcripts
									</MDTypography>
									<MDTypography variant="h4">
										{comparisonData.userAnalysis.totalTranscripts}
									</MDTypography>
								</Grid>
							</Grid>
						</MDBox>
					</Grid>
					<Grid item xs={12} md={6}>
						<MDBox
							p={3}
							borderRadius="lg"
							sx={{
								backgroundColor: 'rgba(156, 39, 176, 0.05)',
								border: '1px solid rgba(156, 39, 176, 0.2)',
							}}
						>
							<MDTypography variant="h6" color="primary" mb={2}>
								<Icon sx={{ verticalAlign: 'middle', mr: 1 }}>smart_toy</Icon>
								Agent Speech Analysis
							</MDTypography>
							<Grid container spacing={2}>
								<Grid item xs={6}>
									<MDTypography variant="caption" color="text">
										Total Words
									</MDTypography>
									<MDTypography variant="h4">
										{comparisonData.agentAnalysis.totalWords.toLocaleString()}
									</MDTypography>
								</Grid>
								<Grid item xs={6}>
									<MDTypography variant="caption" color="text">
										Transcripts
									</MDTypography>
									<MDTypography variant="h4">
										{comparisonData.agentAnalysis.totalTranscripts}
									</MDTypography>
								</Grid>
							</Grid>
						</MDBox>
					</Grid>
				</Grid>
			</Grid>

			{/* Comparison Charts */}
			<Grid item xs={12} md={6}>
				<VerticalBarChart
					title="User Top Words"
					description="Most common words spoken by users"
					height="300px"
					chart={userChartData}
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<VerticalBarChart
					title="Agent Top Words"
					description="Most common words spoken by agents"
					height="300px"
					chart={agentChartData}
				/>
			</Grid>

			{/* Word Chips */}
			<Grid item xs={12} md={6}>
				<MDBox p={2} sx={{ backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
					<MDTypography variant="subtitle2" color="info" mb={2}>
						User Top Phrases
					</MDTypography>
					<MDBox display="flex" flexDirection="column" gap={1}>
						{comparisonData.userAnalysis.topPhrases.slice(0, 5).map((phrase, idx) => (
							<MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center">
								<MDTypography variant="caption">{phrase.phrase}</MDTypography>
								<Chip label={phrase.count} size="small" color="info" variant="outlined" />
							</MDBox>
						))}
					</MDBox>
				</MDBox>
			</Grid>
			<Grid item xs={12} md={6}>
				<MDBox p={2} sx={{ backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
					<MDTypography variant="subtitle2" color="primary" mb={2}>
						Agent Top Phrases
					</MDTypography>
					<MDBox display="flex" flexDirection="column" gap={1}>
						{comparisonData.agentAnalysis.topPhrases.slice(0, 5).map((phrase, idx) => (
							<MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center">
								<MDTypography variant="caption">{phrase.phrase}</MDTypography>
								<Chip label={phrase.count} size="small" color="primary" variant="outlined" />
							</MDBox>
						))}
					</MDBox>
				</MDBox>
			</Grid>
		</Grid>
	)
}

export default ComparisonTab
